import { useRef, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { Switch } from '@ifixit/ui/components/switch';
import { SignUpInput } from './components/SignUpInput';
import { useTranslations } from '@ifixit/i18n/hooks';
import { _jsr } from 'Shared/translations_react';
import { FormError } from '../FormError';
import { CodeInput } from './components/CodeInput';
import { useRegisterCode } from './hooks/useRegisterCode';
import { recaptchaSiteKeyFor } from 'Shared/recaptcha-site-key';
import { Geo } from 'Shared/utils/geo';

interface RegisterCodeProps {
   privacyPolicyUrl: string;
   email: string;
   onJoinComplete?: () => void;
}

function SignUpSwitch({
   value,
   onChange,
   label,
   id,
}: {
   value: boolean;
   onChange: (value: boolean) => void;
   label: string;
   id: string;
}) {
   return (
      <div className="flex items-center w-full gap-2">
         <label htmlFor={id} className="text-sm text-gray-500">
            {label}
         </label>
         <Switch id={id} checked={value} onCheckedChange={onChange} />
      </div>
   );
}

function NewsletterField({
   value,
   onChange,
   id,
   label,
}: {
   value: boolean;
   onChange: (value: boolean) => void;
   id: string;
   label: string;
}) {
   const hasRunGeoLogic = useRef(false);

   useEffect(() => {
      if (!hasRunGeoLogic.current) {
         Geo.runEverywhereButEU(() => {
            onChange(true);
         });
         hasRunGeoLogic.current = true;
      }
   }, [onChange]);

   return (
      <div>
         <SignUpSwitch value={value} onChange={onChange} label={label} id={id} />
      </div>
   );
}

export const RegisterCode = ({ privacyPolicyUrl, email, onJoinComplete }: RegisterCodeProps) => {
   const t = useTranslations('Login');
   const {
      formValues,
      code,
      setCode,
      codeError,
      errors,
      touched,
      isSubmitting,
      handleInputChange,
      handleInputBlur,
      handleSubmit,
      recaptchaPlaceholderRef,
   } = useRegisterCode({ email, privacyPolicyUrl, onJoinComplete });

   const privacyPolicyLink = (
      <a href={privacyPolicyUrl} className="text-gray-900">
         {t('privacyPolicy')}
      </a>
   );

   const termsLink = (
      <a href="/Info/Terms_of_Use" className="text-gray-900">
         {t('terms')}
      </a>
   );

   const emailFieldProvided = !!email;

   return (
      <div className="sign-up-wrapper flex flex-col gap-6 p-8 bg-white border-1 border-base rounded-base text-base w-full md:w-[384px]">
         <form
            id="registerForm"
            onSubmit={e => {
               e.preventDefault();
               void handleSubmit();
            }}
            className="sign-up-form w-full"
         >
            <div className="flex flex-col gap-6">
               <div className={`${touched.email && Boolean(errors.email) ? 'border-red-500' : ''}`}>
                  <SignUpInput
                     field={{
                        name: 'email',
                        value: formValues.email,
                        onChange: (e: ChangeEvent<HTMLInputElement>) =>
                           handleInputChange('email', e.target.value),
                        onBlur: () => handleInputBlur('email'),
                        type: 'email',
                        required: true,
                        autoComplete: 'section-register email',
                        placeholder: 'email@example.com',
                     }}
                     label={t('email')}
                     id="email"
                     isDisabled={emailFieldProvided}
                  />
                  <FormError>{errors.email as any}</FormError>
               </div>
               <div className={`${touched.name && Boolean(errors.name) ? 'border-red-500' : ''}`}>
                  <SignUpInput
                     field={{
                        name: 'name',
                        value: formValues.name,
                        onChange: (e: ChangeEvent<HTMLInputElement>) =>
                           handleInputChange('name', e.target.value),
                        onBlur: () => handleInputBlur('name'),
                        type: 'text',
                        required: true,
                        autoComplete: 'section-register name',
                        placeholder: 'Albert Einstein',
                        minLength: 3,
                        maxLength: 30,
                     }}
                     label={t('displayName')}
                     helperText={t('thisFieldWillBeVisible')}
                     id="reg-username"
                  />
                  <FormError>{errors.name as any}</FormError>
               </div>
               <div className="w-full">
                  <CodeInput value={code} onChange={setCode} inputId="register-code-input" />
                  {codeError && <p className="text-red-500 mt-2 text-sm">{codeError}</p>}
               </div>
               <div className="flex flex-col gap-4 items-start text-md">
                  <p className="font-medium">{t('levelUpYourRepairSkills')}</p>
                  <NewsletterField
                     value={formValues.wantsDigestEmails}
                     onChange={value => handleInputChange('wantsDigestEmails', value)}
                     id="community-yes"
                     label={t('monthlyHighlights')}
                  />
                  <NewsletterField
                     value={formValues.wantsNewsEmails}
                     onChange={value => handleInputChange('wantsNewsEmails', value)}
                     id="newsletter-yes"
                     label={t('weeklyRepairTips')}
                  />
               </div>
               {recaptchaSiteKeyFor('register') && (
                  <div
                     ref={recaptchaPlaceholderRef}
                     className="showRegister js-captcha-container captcha-container"
                  ></div>
               )}
               <button
                  className="w-full button-primary button-lg"
                  type="submit"
                  id="registerBtn"
                  disabled={isSubmitting}
               >
                  {t('createAccountButton')}
               </button>
               <p className="text-sm text-gray-500">
                  {_jsr(
                     /* Translators: %1 and %2 are links to our Privacy Policy and Terms of Use */
                     'By joining iFixit, you agree to our %1 and %2.',
                     privacyPolicyLink,
                     termsLink
                  )}
               </p>
            </div>
         </form>
      </div>
   );
};
